import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFocusTrap } from "./useFocusTrap";

/**
 * A modal dialog (FRONTEND_DESIGN §4.5): over a dimmed page, focus kept inside, Escape or a click on the dim closes
 * it, focus goes back to what opened it. Full screen on narrow screens; scrolls inside when tall.
 */
export function Dialog({
  title,
  onClose,
  footer,
  children,
  testId,
}: {
  title: string;
  onClose(): void;
  footer?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, onClose);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center narrow:items-stretch" data-modal>
      <div className="absolute inset-0 bg-[rgba(28,27,26,0.32)]" aria-hidden onClick={onClose} />
      <div
        ref={box}
        role="dialog"
        aria-modal
        aria-label={title}
        data-testid={testId}
        className="relative mt-[6dvh] flex max-h-[88dvh] w-[min(44rem,calc(100%-2rem))] flex-col rounded-md border border-neutral-200 bg-[var(--surface)] text-sm shadow-[var(--shadow-lg)] narrow:mt-0 narrow:max-h-none narrow:w-full narrow:rounded-none narrow:pt-[env(safe-area-inset-top)] dark:border-neutral-700"
      >
        <header className="flex shrink-0 items-center gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid h-8 w-8 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 touch:h-11 touch:w-11 dark:hover:bg-neutral-800"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-neutral-200 px-4 py-3 narrow:pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-neutral-800">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
